import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { CoworkSessionSummary } from '../../types/cowork';
import CoworkSessionItem from './CoworkSessionItem';
import { i18nService } from '../../services/i18n';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ResolvedIMSession {
  platform: string;
  platformLabel: string;
  instanceName: string;
  groupKey: string;
  conversationLabel: string;
  chatType: 'direct' | 'group';
}

interface IMGroupHeader {
  platform: string;
  platformLabel: string;
  instanceName: string;
}

const IM_PLATFORM_META: Record<string, { label: string; logo?: string }> = {
  wechat: { label: '微信' },
  dingtalk: { label: '钉钉', logo: 'dingding.png' },
  feishu: { label: '飞书', logo: 'feishu.png' },
  telegram: { label: 'Telegram', logo: 'telegram.svg' },
  discord: { label: 'Discord', logo: 'discord.svg' },
  nim: { label: '云信 NIM' },
  wechatwork: { label: '企业微信', logo: 'wecom.svg' },
  qq: { label: 'QQ' },
  whatsapp: { label: 'WhatsApp', logo: 'whatsapp.svg' },
};

const IM_PLATFORM_ALIASES: Record<string, string> = {
  微信: 'wechat',
  wechat: 'wechat',
  钉钉: 'dingtalk',
  釘釘: 'dingtalk',
  dingtalk: 'dingtalk',
  飞书: 'feishu',
  飛書: 'feishu',
  feishu: 'feishu',
  lark: 'feishu',
  telegram: 'telegram',
  discord: 'discord',
  '云信 nim': 'nim',
  '雲信 nim': 'nim',
  nim: 'nim',
  企业微信: 'wechatwork',
  企業微信: 'wechatwork',
  wecom: 'wechatwork',
  wechatwork: 'wechatwork',
  qq: 'qq',
  whatsapp: 'whatsapp',
};

function normalizeIMPlatform(value: string): string | null {
  const normalized = value.trim();
  if (!normalized) return null;
  return IM_PLATFORM_ALIASES[normalized.toLowerCase()]
    || IM_PLATFORM_ALIASES[normalized]
    || (IM_PLATFORM_META[normalized.toLowerCase()] ? normalized.toLowerCase() : null);
}

function isOpaqueExternalIdentifier(value: string | undefined): boolean {
  const normalized = value?.trim() || '';
  return /^(?:ou|on|oc)_[a-z0-9]+$/i.test(normalized)
    || /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(normalized);
}

function parseGeneratedIMTitle(title: unknown): {
  platform: string;
  platformLabel: string;
  instanceName: string;
  conversationLabel: string;
} | null {
  // Current desktop titles use full-width brackets and separator. Accept the
  // ASCII variants as well so Web can group sessions from older clients.
  if (typeof title !== 'string') return null;
  const match = title.trim().match(/^(?:【([^】]+)】|\[([^\]]+)\])\s*(.+?)\s*[｜|]\s*(.+)$/u);
  if (!match) return null;
  const platformLabel = (match[1] || match[2] || '').trim();
  const platform = normalizeIMPlatform(platformLabel);
  const instanceName = match[3]?.trim();
  const conversationLabel = match[4]?.trim();
  if (!platform || !instanceName || !conversationLabel) return null;
  return { platform, platformLabel, instanceName, conversationLabel };
}

function resolveIMSession(session: CoworkSessionSummary): ResolvedIMSession | null {
  const metadata = session.imMetadata;
  const parsedTitle = parseGeneratedIMTitle(session.title);

  if (metadata?.instanceDeletedAt) return null;

  if (metadata) {
    const rawPlatform = typeof metadata.platform === 'string' ? metadata.platform : '';
    const platform = normalizeIMPlatform(rawPlatform) || rawPlatform.trim().toLowerCase();
    if (!platform) return null;
    const platformLabel = IM_PLATFORM_META[platform]?.label || rawPlatform;
    const instanceName = metadata.instanceName?.trim() || metadata.instanceId?.trim();
    if (!instanceName) return null;

    const generatedTitleMatches = parsedTitle
      && parsedTitle.platform === platform
      && parsedTitle.instanceName === instanceName;
    let conversationLabel: string;
    if (generatedTitleMatches) {
      conversationLabel = parsedTitle.conversationLabel;
    } else if (session.title && !/^IM-[a-z0-9_-]+-\d+$/i.test(session.title)) {
      // A manually renamed session remains the child label.
      conversationLabel = session.title;
    } else if (metadata.conversationName?.trim()) {
      conversationLabel = metadata.conversationName.trim();
    } else if (metadata.chatType === 'direct') {
      const senderName = metadata.lastSenderName?.trim();
      conversationLabel = senderName && !isOpaqueExternalIdentifier(senderName) ? senderName : '私聊';
    } else {
      conversationLabel = '群聊';
    }

    return {
      platform,
      platformLabel,
      instanceName,
      groupKey: `${session.clientId || ''}:${platform}:${metadata.instanceId || instanceName}`,
      conversationLabel,
      chatType: metadata.chatType || (/^(?:私聊|private|direct)$/i.test(conversationLabel) ? 'direct' : 'group'),
    };
  }

  if (!parsedTitle) return null;
  return {
    platform: parsedTitle.platform,
    platformLabel: IM_PLATFORM_META[parsedTitle.platform]?.label || parsedTitle.platformLabel,
    instanceName: parsedTitle.instanceName,
    groupKey: `${session.clientId || ''}:${parsedTitle.platform}:${parsedTitle.instanceName}`,
    conversationLabel: parsedTitle.conversationLabel,
    chatType: /^(?:私聊|private|direct)$/i.test(parsedTitle.conversationLabel) ? 'direct' : 'group',
  };
}

const IMPlatformIcon: React.FC<{ platform: string; label: string }> = ({ platform, label }) => {
  const logo = IM_PLATFORM_META[platform]?.logo;
  if (logo) {
    return <img src={logo} alt={label} className="h-4 w-4 shrink-0 object-contain" />;
  }
  if (platform === 'wechat') {
    return (
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#07c160] text-white">
        <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden="true">
          <ellipse cx="6.2" cy="6.1" rx="4.2" ry="3.4" fill="currentColor" />
          <path d="M3.4 8.5 2.8 11l2.4-1.5" fill="currentColor" />
          <ellipse cx="10.5" cy="9.4" rx="3.4" ry="2.7" fill="currentColor" opacity=".9" />
          <path d="m12.2 11.5.7 2-2-1.2" fill="currentColor" opacity=".9" />
          <circle cx="4.8" cy="5.8" r=".45" fill="#07c160" />
          <circle cx="7.4" cy="5.8" r=".45" fill="#07c160" />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-claude-accent/10 text-[8px] font-semibold text-claude-accent">
      {label.slice(0, 1)}
    </span>
  );
};

const IMConversationIcon: React.FC<{ chatType: 'direct' | 'group' }> = ({ chatType }) => (
  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-black/[0.04] text-claude-textSecondary dark:bg-white/[0.07] dark:text-claude-darkTextSecondary">
    <svg viewBox="0 0 16 16" className="h-3 w-3" aria-hidden="true">
      {chatType === 'group' ? (
        <>
          <circle cx="10.6" cy="5.1" r="1.65" fill="currentColor" opacity=".5" />
          <path d="M7.9 12.6c.3-1.8 1.4-2.75 3-2.75 1.45 0 2.55.9 2.85 2.75H7.9Z" fill="currentColor" opacity=".5" />
          <circle cx="5.55" cy="5.9" r="2" fill="currentColor" />
          <path d="M1.65 13c.35-2.3 1.75-3.5 3.9-3.5s3.55 1.2 3.9 3.5h-7.8Z" fill="currentColor" />
        </>
      ) : (
        <>
          <circle cx="8" cy="5.3" r="2.1" fill="currentColor" />
          <path d="M3.8 13c.4-2.45 1.9-3.75 4.2-3.75s3.8 1.3 4.2 3.75H3.8Z" fill="currentColor" />
        </>
      )}
    </svg>
  </span>
);

interface CoworkSessionListProps {
  sessions: CoworkSessionSummary[];
  currentSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onTogglePin: (sessionId: string, pinned: boolean) => void;
  onRenameSession: (sessionId: string, title: string) => void;
  batchMode: boolean;
  onBatchDelete: (sessionIds: string[]) => void;
}

const CoworkSessionList: React.FC<CoworkSessionListProps> = ({
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onTogglePin,
  onRenameSession,
  batchMode,
  onBatchDelete,
}) => {
  const unreadSessionIds = useSelector((state: RootState) => state.cowork.unreadSessionIds);
  const unreadSessionIdSet = useMemo(() => new Set(unreadSessionIds), [unreadSessionIds]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConfirmBatchDelete, setShowConfirmBatchDelete] = useState(false);

  const sortedSessions = useMemo(() => {
    const sortByRecentActivity = (a: CoworkSessionSummary, b: CoworkSessionSummary) => {
      if (b.updatedAt !== a.updatedAt) {
        return b.updatedAt - a.updatedAt;
      }
      return b.createdAt - a.createdAt;
    };

    const pinnedSessions = sessions
      .filter((session) => session.pinned)
      .sort(sortByRecentActivity);
    const unpinnedSessions = sessions
      .filter((session) => !session.pinned)
      .sort(sortByRecentActivity);
    return [...pinnedSessions, ...unpinnedSessions];
  }, [sessions]);

  // Relay mirrors independent desktop Agent contexts. Presentation alone is
  // grouped into a platform + bot tree, matching the desktop client without
  // changing session ownership or persistence on Web.
  const { displaySessions, imGroupHeaders, resolvedIMSessions } = useMemo(() => {
    if (batchMode) {
      return {
        displaySessions: sortedSessions,
        imGroupHeaders: new Map<string, IMGroupHeader>(),
        resolvedIMSessions: new Map<string, ResolvedIMSession>(),
      };
    }

    const regularSessions: CoworkSessionSummary[] = [];
    const groups = new Map<string, { header: IMGroupHeader; sessions: CoworkSessionSummary[] }>();
    const resolvedIMSessions = new Map<string, ResolvedIMSession>();

    for (const session of sortedSessions) {
      const resolved = resolveIMSession(session);
      if (!resolved) {
        regularSessions.push(session);
        continue;
      }
      resolvedIMSessions.set(session.id, resolved);
      const group = groups.get(resolved.groupKey) || {
        header: {
          platform: resolved.platform,
          platformLabel: resolved.platformLabel,
          instanceName: resolved.instanceName,
        },
        sessions: [],
      };
      group.sessions.push(session);
      groups.set(resolved.groupKey, group);
    }

    const imGroupHeaders = new Map<string, IMGroupHeader>();
    const groupedSessions: CoworkSessionSummary[] = [];
    for (const group of groups.values()) {
      const firstSession = group.sessions[0];
      if (!firstSession) continue;
      imGroupHeaders.set(firstSession.id, group.header);
      groupedSessions.push(...group.sessions);
    }

    return {
      displaySessions: [...groupedSessions, ...regularSessions],
      imGroupHeaders,
      resolvedIMSessions,
    };
  }, [batchMode, sortedSessions]);

  // Reset selection when exiting batch mode
  React.useEffect(() => {
    if (!batchMode) {
      setSelectedIds(new Set());
      setShowConfirmBatchDelete(false);
    }
  }, [batchMode]);

  const toggleSelect = (sessionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const allSelected = sessions.length > 0 && selectedIds.size === sessions.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sessions.map((s) => s.id)));
    }
  };

  const handleBatchDelete = () => {
    if (selectedIds.size === 0) return;
    setShowConfirmBatchDelete(true);
  };

  const confirmBatchDelete = () => {
    onBatchDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
    setShowConfirmBatchDelete(false);
  };

  if (sessions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('coworkNoSessions')}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-0.5">
      {/* Batch action bar */}
      {batchMode && (
        <div className="flex items-center justify-between px-2 py-1.5">
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-accent transition-colors"
          >
            {allSelected ? i18nService.t('batchDeselectAll') : i18nService.t('batchSelectAll')}
          </button>
          <button
            type="button"
            onClick={handleBatchDelete}
            disabled={selectedIds.size === 0}
            className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
              selectedIds.size > 0
                ? 'text-red-500 hover:bg-red-500/10'
                : 'text-claude-textSecondary/40 dark:text-claude-darkTextSecondary/40 cursor-not-allowed'
            }`}
          >
            {i18nService.t('batchDelete')}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
        </div>
      )}

      {displaySessions.map((session) => {
        const header = imGroupHeaders.get(session.id);
        const resolvedIMSession = resolvedIMSessions.get(session.id);
        return (
          <React.Fragment key={session.id}>
            {header && (
              <div className="flex min-w-0 items-center gap-1.5 px-2 pb-1 pt-2 text-[12px] font-medium leading-[1.5] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                <IMPlatformIcon platform={header.platform} label={header.platformLabel} />
                <span className="truncate">【{header.platformLabel}】{header.instanceName}</span>
              </div>
            )}
            <div className={`flex min-w-0 items-center gap-1 ${resolvedIMSession ? 'ml-4 border-l border-claude-border/60 pl-1 dark:border-claude-darkBorder/60' : ''}`}>
          {batchMode && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleSelect(session.id);
              }}
              className="shrink-0 ml-1 flex items-center justify-center"
            >
              <div
                className={`w-4 h-4 rounded border transition-colors flex items-center justify-center ${
                  selectedIds.has(session.id)
                    ? 'bg-claude-accent border-claude-accent'
                    : 'dark:border-claude-darkBorder border-claude-border hover:border-claude-accent/50'
                }`}
              >
                {selectedIds.has(session.id) && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </button>
          )}
          <div className="flex-1 min-w-0">
            <CoworkSessionItem
              session={session}
              displayTitle={resolvedIMSession?.conversationLabel}
              leadingIcon={resolvedIMSession ? <IMConversationIcon chatType={resolvedIMSession.chatType} /> : undefined}
              hasUnread={unreadSessionIdSet.has(session.id)}
              isActive={session.id === currentSessionId}
              onSelect={() => {
                if (batchMode) {
                  toggleSelect(session.id);
                } else {
                  onSelectSession(session.id);
                }
              }}
              onDelete={() => onDeleteSession(session.id)}
              onTogglePin={(pinned) => onTogglePin(session.id, pinned)}
              onRename={(title) => onRenameSession(session.id, title)}
            />
          </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* Batch Delete Confirmation Modal */}
      {showConfirmBatchDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowConfirmBatchDelete(false)}
        >
          <div
            className="w-[calc(100%-2rem)] max-w-sm dark:bg-claude-darkSurface bg-claude-surface rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-500" />
              </div>
              <h2 className="text-base font-semibold dark:text-claude-darkText text-claude-text">
                {i18nService.t('batchDeleteConfirmTitle')}
              </h2>
            </div>
            <div className="px-5 pb-4">
              <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {i18nService.t('batchDeleteConfirmMessage').replace('{count}', String(selectedIds.size))}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t dark:border-claude-darkBorder border-claude-border">
              <button
                onClick={() => setShowConfirmBatchDelete(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                {i18nService.t('cancel')}
              </button>
              <button
                onClick={confirmBatchDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                {i18nService.t('batchDelete')} ({selectedIds.size})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoworkSessionList;
